
import React, { useContext, useState, useEffect } from 'react';
import { auth, db, secondaryAuth } from '../firebase';
import {
  signInWithEmailAndPassword as _realSignIn,
  signOut as _realSignOut,
  createUserWithEmailAndPassword as _realCreateUser,
} from 'firebase/auth';
import {
  doc as _realDoc,
  getDoc as _realGetDoc,
  setDoc as _realSetDoc,
  updateDoc as _realUpdateDoc,
  serverTimestamp as _realServerTimestamp,
} from 'firebase/firestore';
import {
  normalizeRole,
  isAdminRole,
  isFacilitatorRole,
  isAssessorRole,
  isExternalRole,
  isExternalFacilitatorRole,
  isExternalViewerRole
} from '../services/roleManagementService';
import { logAuth, logError, logDebug } from '../utils/secureLogger';
import { KNOWN_ADMIN_EMAILS } from '../config/adminConfig';

const friendlyAuthError = (error) => {
  const code = error?.code || '';
  const map = {
    'auth/wrong-password':          'Incorrect password. Please try again.',
    'auth/invalid-credential':      'Incorrect email or password. Please try again.',
    'auth/invalid-email':           'Please enter a valid email address.',
    'auth/user-not-found':          'No account found with this email address.',
    'auth/user-disabled':           'This account has been disabled. Please contact support.',
    'auth/too-many-requests':       'Too many failed attempts. Please wait a few minutes and try again.',
    'auth/network-request-failed':  'Network error. Please check your connection and try again.',
    'auth/popup-closed-by-user':    'Sign-in was cancelled.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
    'auth/email-already-in-use':    'An account with this email already exists.',
    'auth/weak-password':           'Password must be at least 6 characters.',
    'auth/requires-recent-login':   'Please sign out and sign in again to complete this action.',
    'auth/expired-action-code':     'This link has expired. Please request a new one.',
    'auth/invalid-action-code':     'This link is invalid or has already been used.',
  };
  return map[code]
    || error?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[^)]+\)\.?/g, '').trim()
    || 'Something went wrong. Please try again.';
};

// When REACT_APP_USE_MOCK_DATA=true the auth/db objects are in-memory fakes that
// expose the Firebase v8-style API. Call them directly instead of using v9 SDK
// functions which talk to real Firebase regardless of the auth/db object passed.
const _isMock = process.env.NODE_ENV === 'development' &&
                process.env.REACT_APP_USE_MOCK_DATA === 'true';

const signInWithEmailAndPassword = _isMock
  ? (a, email, pass) => a.signInWithEmailAndPassword(email, pass)
  : _realSignIn;
const signOut = _isMock
  ? (a) => a.signOut()
  : _realSignOut;
const createUserWithEmailAndPassword = _isMock
  ? (a, email, pass) => a.createUserWithEmailAndPassword(email, pass)
  : _realCreateUser;
const doc = _isMock
  ? (dbInst, ...segs) => dbInst.doc(segs.join('/'))
  : _realDoc;
const getDoc = _isMock
  ? (ref) => ref.get()
  : _realGetDoc;
const setDoc = _isMock
  ? (ref, data) => ref.set(data)
  : _realSetDoc;
const updateDoc = _isMock
  ? (ref, data) => ref.update(data)
  : _realUpdateDoc;
const serverTimestamp = _isMock
  ? () => new Date()
  : _realServerTimestamp;

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Firestore
  const fetchUserProfile = async (user) => {
    if (!user) {
      setUserProfile(null);
      return null;
    }

    try {
      logDebug('fetchUserProfile', { uid: user.uid });
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const profile = userDoc.data();
        // Normalize assignedPrograms: some docs (e.g. admins) only have programId (string).
        // Ensure all roles get a consistent array so downstream code doesn't need to branch.
        const assignedPrograms =
          Array.isArray(profile.assignedPrograms) && profile.assignedPrograms.length > 0
            ? profile.assignedPrograms
            : profile.programId
            ? [profile.programId]
            : [];
        setUserProfile({
          ...profile,
          role: normalizeRole(profile.role),
          assignedPrograms,
        });
        return profile;
      } else {
        logError('User profile not found in Firestore', { uid: user.uid });
        setUserProfile(null);
        return null;
      }
    } catch (error) {
      logError('Error fetching user profile', { message: error.message });
      setUserProfile(null);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      logAuth('login_attempt', { email });

      const result = await signInWithEmailAndPassword(auth, email, password);
      logAuth('login_firebase_success', { uid: result.user.uid });

      const profile = await fetchUserProfile(result.user);

      const isActive = profile?.isActive === true || profile?.status === 'active';
      if (profile && !isActive) {
        await signOut(auth);
        throw new Error('Account is deactivated. Contact administrator.');
      }

      if (!profile && !KNOWN_ADMIN_EMAILS.includes(email.toLowerCase())) {
        await signOut(auth);
        throw new Error('User profile not found. Contact administrator.');
      }

      // Update login timestamp and migrate legacy roles on every login
      if (profile) {
        try {
          const updateData = {
            lastLoginAt: serverTimestamp()
          };

          // Mark email as verified on first successful login
          if (profile.emailVerified !== true || profile.isVerified !== true) {
            updateData.emailVerified = true;
            updateData.isVerified = true;
            updateData.emailVerifiedAt = serverTimestamp();
            updateData.firstLoginAt = profile.firstLoginAt || serverTimestamp();
          }

          await updateDoc(doc(db, 'users', result.user.uid), updateData);
          await fetchUserProfile(result.user);
        } catch (updateError) {
          logError('Failed to update login status', { message: updateError.message });
        }
      }

      return result;
    } catch (error) {
      logError('Login error', { code: error.code, message: error.message });
      throw new Error(friendlyAuthError(error));
    }
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Create user function (admin only)
  const createUser = async (userData) => {
    try {
      // SECURITY FIX: Enforce admin password verification
      if (!userData.adminPassword) {
        throw new Error('Admin password verification is required to create users');
      }
      
      const adminEmail = currentUser?.email;
      if (adminEmail && userData.adminPassword) {
        try {
          // Verify admin credentials using secondary auth to avoid session disruption
          await signInWithEmailAndPassword(secondaryAuth, adminEmail, userData.adminPassword);
          await signOut(secondaryAuth);
        } catch (verifyError) {
          throw new Error('Invalid admin password. User creation cancelled.');
        }
      } else {
        throw new Error('Admin authentication required for user creation');
      }
      
      // Store current admin info before creating new user
      const adminUser = currentUser;
      
      // Create Firebase Auth user using secondary auth to prevent session issues
      const result = await createUserWithEmailAndPassword(secondaryAuth, userData.email, userData.password);
      
      // Sign out from secondary auth to clean up
      await signOut(secondaryAuth);
      
      // Create user profile in Firestore with all provided data
      const { adminPassword, password, ...userDataWithoutSecrets } = userData;
      
      const newUserProfile = {
        // Preserve all additional fields from userData (except sensitive ones)
        ...userDataWithoutSecrets,
        // Override critical fields that should not be user-controlled
        uid: result.user.uid, // Ensure UID matches Firebase Auth
        email: userData.email, // Ensure email consistency
        displayName: userData.displayName,
        role: userData.role,
        isActive: true,
        createdAt: serverTimestamp(), // Always use server timestamp
        createdBy: adminUser?.uid || 'system' // Track who created the user
      };

      await setDoc(doc(db, 'users', result.user.uid), newUserProfile);

      return { user: result.user, userData: newUserProfile };
    } catch (error) {
      if (error.code) throw new Error(friendlyAuthError(error));
      throw error;
    }
  };

  // Check if current user has specific role
  const hasRole = (role) => {
    return normalizeRole(userProfile?.role) === normalizeRole(role);
  };

  // Check if current user is admin
  const isAdmin = () => {
    // Check profile role first
    if (isAdminRole(userProfile?.role)) {
      return true;
    }
    
    // If no profile, check if it's a known admin email (bootstrap accounts only)
    return currentUser && KNOWN_ADMIN_EMAILS.includes(currentUser.email?.toLowerCase());
  };

  // Check if current user is facilitator
  const isFacilitator = () => {
    return isFacilitatorRole(userProfile?.role);
  };

  // Check if current user is learner
  const isLearner = () => {
    return userProfile?.role === 'learner';
  };

  // Check if current user is manager (new role)
  const isManager = () => {
    return userProfile?.role === 'manager';
  };

  // Check if current user is assessor (new role)
  const isAssessor = () => {
    return isAssessorRole(userProfile?.role);
  };

  // Check if current user is moderator (new role)
  const isModerator = () => {
    return userProfile?.role === 'moderator';
  };

  // Check if current user is QA (Quality Assurance)
  const isQA = () => {
    return userProfile?.role === 'qa';
  };

  // Check if current user is any external role variant
  const isExternal = () => {
    return isExternalRole(userProfile?.role);
  };

  // Check if current user is external facilitator
  const isExternalFacilitator = () => {
    return isExternalFacilitatorRole(userProfile?.role);
  };

  // Check if current user is external viewer
  const isExternalViewer = () => {
    return isExternalViewerRole(userProfile?.role);
  };

  // Check if current user has admin or manager privileges
  const isAdminOrManager = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'manager';
  };

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    createUser,
    hasRole,
    isAdmin,
    isFacilitator,
    isLearner,
    isManager,
    isAssessor,
    isModerator,
    isQA,
    isExternal,
    isExternalFacilitator,
    isExternalViewer,
    isAdminOrManager,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
